import { html, type TemplateResult } from "lit";

export type PfmTreeListNode = {
  nodeKey: string;
  title: string;
  level: number;
  childCount: number;
  crowded?: boolean;
  children?: PfmTreeListNode[];
};

export type PfmTreeListPayload = {
  totalNodes: number;
  topLevelSpokes: number;
  maxDepth: number;
  levelCounts: Record<string, number>;
  nodesWithParent: number;
  flatDelivery: boolean;
  densityViolations: Array<{
    parentLabel?: string;
    parent_node_key?: string | null;
    childCount?: number;
    child_count?: number;
    maxAllowed?: number;
    max_allowed?: number;
  }>;
  crowdedLevelCount: number;
  roots: PfmTreeListNode[];
};

export function parsePfmTreeListPayload(raw: unknown): PfmTreeListPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const rootsRaw = (o.roots ?? o.roots) as unknown;
  const roots = Array.isArray(rootsRaw) ? rootsRaw.map(parseTreeNode).filter(Boolean) as PfmTreeListNode[] : [];
  const levelCountsRaw = o.levelCounts ?? o.level_counts;
  const levelCounts =
    levelCountsRaw && typeof levelCountsRaw === "object"
      ? Object.fromEntries(
          Object.entries(levelCountsRaw as Record<string, unknown>).map(([k, v]) => [k, Number(v) || 0]),
        )
      : {};
  return {
    totalNodes: Number(o.totalNodes ?? o.total_nodes ?? 0),
    topLevelSpokes: Number(o.topLevelSpokes ?? o.top_level_spokes ?? 0),
    maxDepth: Number(o.maxDepth ?? o.max_depth ?? 0),
    levelCounts,
    nodesWithParent: Number(o.nodesWithParent ?? o.nodes_with_parent ?? 0),
    flatDelivery: Boolean(o.flatDelivery ?? o.flat_delivery),
    densityViolations: Array.isArray(o.densityViolations ?? o.density_violations)
      ? ((o.densityViolations ?? o.density_violations) as PfmTreeListPayload["densityViolations"])
      : [],
    crowdedLevelCount: Number(o.crowdedLevelCount ?? o.crowded_level_count ?? 0),
    roots,
  };
}

function parseTreeNode(raw: unknown): PfmTreeListNode | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const nodeKey = String(o.nodeKey ?? o.node_key ?? "").trim();
  if (!nodeKey) return null;
  const childrenRaw = o.children;
  const children = Array.isArray(childrenRaw)
    ? childrenRaw.map(parseTreeNode).filter(Boolean) as PfmTreeListNode[]
    : [];
  return {
    nodeKey,
    title: String(o.title ?? nodeKey).trim() || nodeKey,
    level: Number(o.level ?? 1),
    childCount: Number(o.childCount ?? o.child_count ?? children.length),
    crowded: Boolean(o.crowded),
    children,
  };
}

function levelSummary(levelCounts: Record<string, number>): string {
  const entries = Object.entries(levelCounts)
    .map(([level, count]) => [Number(level), count] as const)
    .filter(([level]) => Number.isFinite(level))
    .sort((a, b) => a[0] - b[0]);
  if (!entries.length) return "—";
  return entries.map(([level, count]) => `L${level}: ${count}`).join(" · ");
}

function renderTreeNode(node: PfmTreeListNode, depth: number): TemplateResult {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const crowded = node.crowded || node.childCount > 15;
  const label = node.title || node.nodeKey;
  return html`
    <li class="pfm-treelist__item ${crowded ? "pfm-treelist__item--crowded" : ""}">
      <div class="pfm-treelist__row" style="padding-left: ${Math.min(depth, 8) * 14}px">
        ${hasChildren
          ? html`<span class="pfm-treelist__branch">▸</span>`
          : html`<span class="pfm-treelist__leaf">•</span>`}
        <span class="pfm-treelist__title">${label}</span>
        ${node.childCount > 0
          ? html`<span class="pfm-treelist__meta">${node.childCount} child${node.childCount === 1 ? "" : "ren"}</span>`
          : ""}
        ${crowded ? html`<span class="pfm-treelist__warn">&gt;15</span>` : ""}
      </div>
      ${hasChildren
        ? html`<ul class="pfm-treelist__children">
            ${node.children!.map((child) => renderTreeNode(child, depth + 1))}
          </ul>`
        : ""}
    </li>
  `;
}

export function renderPfmTreeListPanel(payload: PfmTreeListPayload | null): TemplateResult {
  if (!payload || payload.totalNodes <= 0) {
    return html`<div class="pfm-treelist pfm-treelist--empty">No treelist data yet.</div>`;
  }

  const violationLines = payload.densityViolations.slice(0, 6).map((v) => {
    const label = String(v.parentLabel ?? v.parent_node_key ?? "(top level)");
    const count = Number(v.childCount ?? v.child_count ?? 0);
    const max = Number(v.maxAllowed ?? v.max_allowed ?? 15);
    return `${label}: ${count} (max ${max})`;
  });

  return html`
    <div class="pfm-treelist">
      <div class="pfm-treelist__summary">
        <strong>Treelist structure</strong>
        <span>${payload.totalNodes} nodes · ${payload.topLevelSpokes} top-level spokes · depth ${payload.maxDepth}</span>
        <span>${levelSummary(payload.levelCounts)}</span>
        ${payload.flatDelivery
          ? html`<span class="pfm-treelist__alert">
              Flat delivery detected — every node lacks parentNodeKey. Ask the agent to reorganize into a multi-level treelist (≤15 children per level).
            </span>`
          : ""}
        ${payload.crowdedLevelCount > 0
          ? html`<span class="pfm-treelist__warn-block">
              ${payload.crowdedLevelCount} crowded parent${payload.crowdedLevelCount === 1 ? "" : "s"}:
              ${violationLines.join(" · ")}
            </span>`
          : ""}
      </div>
      <ul class="pfm-treelist__roots">
        ${payload.roots.map((root) => renderTreeNode(root, 0))}
      </ul>
    </div>
  `;
}
